import { cva, type VariantProps } from "class-variance-authority";
import { ChevronRight } from "lucide-react";
import type { ComponentType, ReactNode } from "react";

const titleVariants = cva("truncate text-lg text-text-primary", {
  variants: {
    titleType: {
      title: "font-display",
      filename: "font-mono",
    },
  },
  defaultVariants: {
    titleType: "title",
  },
});

/** Props for a link wrapper component */
export type LinkWrapperProps = {
  href: string;
  children: ReactNode;
  className?: string;
};

/** Default link wrapper using a plain anchor element */
function DefaultLinkWrapper({ href, children, className }: LinkWrapperProps) {
  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
}

type Props = VariantProps<typeof titleVariants> & {
  title: string;
  subtitle?: string | null;
  linkHref?: string;
  leftContent?: ReactNode;
  rightContent?: ReactNode;
  "data-testid"?: string;
  /** Custom link wrapper component (defaults to plain anchor) */
  LinkWrapper?: ComponentType<LinkWrapperProps>;
};

export function MediaItemContent({
  title,
  subtitle,
  linkHref,
  leftContent,
  rightContent,
  titleType,
  "data-testid": testId,
  LinkWrapper = DefaultLinkWrapper,
}: Props) {
  const textContent = (
    <span className="flex min-w-0 flex-1 flex-col">
      <span className={titleVariants({ titleType })} data-testid={testId}>
        {title}
      </span>
      {subtitle && (
        <span className="truncate text-sm text-text-muted">{subtitle}</span>
      )}
    </span>
  );

  const linkContent = linkHref ? (
    <LinkWrapper
      href={linkHref}
      className="group -mx-2 -my-1 flex min-w-0 flex-1 items-center gap-3 rounded-md px-2 py-1"
    >
      {textContent}
      <ChevronRight
        className="h-4 w-4 shrink-0 text-text-muted opacity-0 transition-opacity duration-150 group-hover:opacity-100"
        aria-hidden="true"
      />
    </LinkWrapper>
  ) : (
    textContent
  );

  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      {leftContent}
      {linkContent}
      {rightContent}
    </div>
  );
}
